import { Loader2, Plus } from "lucide-react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui";
import { PropertySchemaAssociationsList, usePropertySchemaAssociationsForm } from "~/features/admin/propertySchemas";
import { useRecordMetadataSchemaEdit } from "~/features/admin/recordMetadataSchema/hooks";
import type {
  RecordMetadataSchemaAssociationRaw,
  RecordMetadataSchemaFormData,
} from "~/features/admin/recordMetadataSchema/types";

interface RecordMetadataSchemaFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  id: string;
  /** Existing schemas to prefill on mount. */
  prefilledAssociations: RecordMetadataSchemaAssociationRaw[];
}

const FORM_ID = "record-metadata-schema-form";

export function RecordMetadataSchemaFormDialog({
  open,
  onOpenChange,
  id,
  prefilledAssociations,
}: RecordMetadataSchemaFormDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {`${t("ui.edit")} ${t("model.propertySchemaSet.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <FormBody id={id} prefilledAssociations={prefilledAssociations} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

interface FormBodyProps {
  id: string;
  prefilledAssociations: RecordMetadataSchemaAssociationRaw[];
  onClose: () => void;
}

function FormBody({ id, prefilledAssociations, onClose }: FormBodyProps) {
  const { t } = useTranslation();
  const methods = useForm<RecordMetadataSchemaFormData>({
    defaultValues: { id, propertySchemaAssociations: [] },
  });

  const schemas = usePropertySchemaAssociationsForm<RecordMetadataSchemaFormData>(methods.control);

  // Seed the field array once per record. `prefilledAssociations` / `schemas`
  // get fresh identities every render, so depending on them would reseed on
  // every keystroke and clobber edits; reseeding only when the record `id`
  // changes is the intended invariant. The dialog also remounts on each open
  // (Radix unmounts closed content), so reopening picks up refetched data.
  useEffect(() => {
    schemas.setDefaults(prefilledAssociations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { submit, processing } = useRecordMetadataSchemaEdit({
    onSuccess: () => {
      methods.reset();
      onClose();
    },
  });

  const onSubmit = (data: RecordMetadataSchemaFormData) => {
    submit({ ...data, id });
  };

  return (
    <FormProvider {...methods}>
      <div className="max-h-[65vh] overflow-y-auto pr-1">
        <form id={FORM_ID} onSubmit={methods.handleSubmit(onSubmit)} className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-16-bold capitalize text-fg-1">{t("model.propertySchema.modelNames")}</h2>
            <Button type="button" variant="outline" size="sm" onClick={schemas.append}>
              <Plus />
              <span>{t("feature.dataImportProjects.form.addPropertySchema")}</span>
            </Button>
          </div>
          <PropertySchemaAssociationsList fields={schemas.fields} onRemove={schemas.remove} onMove={schemas.move} />
        </form>
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="outline" disabled={processing}>
            {t("ui.cancel")}
          </Button>
        </DialogClose>
        <Button type="submit" form={FORM_ID} disabled={processing}>
          {processing && <Loader2 className="animate-spin" />}
          <span>{t("ui.save")}</span>
        </Button>
      </DialogFooter>
    </FormProvider>
  );
}
